import { Helmet } from 'react-helmet-async';

interface SEOHeadProps {
  title?: string;
  description?: string;
  keywords?: string;
  image?: string;
  url?: string;
  type?: 'website' | 'article' | 'profile';
  author?: string;
  publishedTime?: string;
  modifiedTime?: string;
  section?: string;
  tags?: string[];
  structuredData?: object;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title = 'Belednai Technology - South Sudan\'s Premier Tech Company',
  description = 'Professional technology solutions including websites, custom software, email systems, and digital marketing services for South Sudanese businesses. Transform your business with cutting-edge technology.',
  keywords = 'South Sudan technology, website development, custom software, email solutions, digital marketing, Juba tech company, software development South Sudan',
  image = 'https://belednaitech.com/og-image.png',
  url = 'https://belednaitech.com',
  type = 'website',
  author = 'Belednai Technology',
  publishedTime,
  modifiedTime,
  section,
  tags = [],
  structuredData,
}) => {
  const fullTitle = title.includes('Belednai Technology') ? title : `${title} | Belednai Technology`;
  const canonicalUrl = url + (typeof window !== 'undefined' ? window.location.pathname : '');

  const defaultStructuredData = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'Belednai Technology',
    url: 'https://belednaitech.com',
    logo: 'https://belednaitech.com/logo.png',
    description: 'South Sudan\'s premier technology company providing websites, custom software, email solutions, and digital marketing services.',
    address: {
      '@type': 'PostalAddress',
      streetAddress: 'Juba',
      addressLocality: 'Juba',
      addressCountry: 'SS'
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+211-928-446-544',
      contactType: 'customer service',
      email: 'info@belednai.com'
    },
    sameAs: [
      'https://www.facebook.com/belednaitech',
      'https://www.linkedin.com/company/belednaitech',
      'https://twitter.com/belednaitech'
    ],
    foundingDate: '2019',
    foundingLocation: 'Juba, South Sudan',
    areaServed: 'South Sudan',
    offers: {
      '@type': 'Offer',
      category: 'Technology Services',
      itemOffered: [
        {
          '@type': 'Service',
          name: 'Website Development',
          description: 'Professional, responsive websites that showcase your business'
        },
        {
          '@type': 'Service',
          name: 'Custom Software Development',
          description: 'Tailored software solutions to streamline business operations'
        },
        {
          '@type': 'Service',
          name: 'Email Solutions',
          description: 'Professional email systems for business communication'
        },
        {
          '@type': 'Service',
          name: 'Digital Marketing',
          description: 'Strategic marketing campaigns to grow your brand'
        }
      ]
    }
  };

  const jsonLd = structuredData || defaultStructuredData;

  return (
    <Helmet>
      {/* Primary Meta Tags */}
      <title>{fullTitle}</title>
      <meta name="title" content={fullTitle} />
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="author" content={author} />
      <meta name="robots" content="index, follow" />
      <meta name="language" content="English" />
      <meta name="geo.region" content="SS" />
      <meta name="geo.placename" content="Juba, South Sudan" />
      <link rel="canonical" href={canonicalUrl} />

      {/* Open Graph / Facebook */}
      <meta property="og:type" content={type} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:image" content={image} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:site_name" content="Belednai Technology" />
      <meta property="og:locale" content="en_US" />
      
      {/* Article specific Open Graph tags */}
      {type === 'article' && (
        <>
          {author && <meta property="article:author" content={author} />}
          {publishedTime && <meta property="article:published_time" content={publishedTime} />}
          {modifiedTime && <meta property="article:modified_time" content={modifiedTime} />}
          {section && <meta property="article:section" content={section} />}
          {tags.map(tag => (
            <meta key={tag} property="article:tag" content={tag} />
          ))}
        </>
      )}

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:url" content={canonicalUrl} />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={image} />
      <meta name="twitter:site" content="@belednaitech" />
      <meta name="twitter:creator" content="@belednaitech" />

      {/* Additional SEO Meta Tags */}
      <meta name="theme-color" content="#2563eb" />
      <meta name="msapplication-TileColor" content="#2563eb" />
      <meta name="apple-mobile-web-app-capable" content="yes" />
      <meta name="apple-mobile-web-app-status-bar-style" content="default" />
      <meta name="apple-mobile-web-app-title" content="Belednai Tech" />

      {/* Structured Data */}
      <script type="application/ld+json">
        {JSON.stringify(jsonLd)}
      </script>

      {/* Preconnect for performance */}
      <link rel="preconnect" href="https://fonts.googleapis.com" />
      <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="anonymous" />
      <link rel="dns-prefetch" href="https://fonts.googleapis.com" />
      <link rel="dns-prefetch" href="https://fonts.gstatic.com" />
    </Helmet>
  );
}; 